import React, { useEffect, useRef, useState } from "react";
import DriverDropdown from "../Utils/DriverDropdown";

import {
  KycImageUpload,
  KycNavigation,
  KycStepShell,
  KycTextField,
} from "./KycFormFields";

// ============================================================
// PINCODE HELPERS
// ============================================================

function extractPincodeRows(data) {
  const candidates = [
    data?.response,
    data?.data,
    data?.response?.data,
    data?.response?.items,
    data?.data?.response,
    data?.items,
  ];

  return candidates.find(Array.isArray) || [];
}

function normalizePincode(item) {
  return {
    id: String(
      item?.id ??
        item?.pinCodeId ??
        item?.pincodeId ??
        item?.pin_code_id ??
        item?.pk_pincode_id ??
        item?.pk_pin_code_id ??
        "",
    ).trim(),

    pinCode: String(
      item?.pinCode ??
        item?.pincode ??
        item?.pin_code ??
        item?.postalCode ??
        item?.postal_code ??
        item?.code ??
        "",
    ).trim(),

    officeName: String(item?.office_name ?? item?.officeName ?? "").trim(),

    officeType: String(item?.office_type ?? item?.officeType ?? "").trim(),

    circle: String(item?.circle ?? "").trim(),
    region: String(item?.region ?? "").trim(),
    division: String(item?.division ?? "").trim(),
  };
}

// ============================================================
// STEP 1 - ADDRESS
// ============================================================

function AddressStep({
  form,
  onFieldChange,
  drivers,
  editKycDriver,
  handleDriverSelect,
  getPincodeDetails,
  onSubmit,
  loading,
}) {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const getPincodeRef = useRef(getPincodeDetails);
  getPincodeRef.current = getPincodeDetails;

  useEffect(() => {
    const word = String(form.pinCode || "").trim();

    if (
      !word ||
      form.pinCodeId ||
      typeof getPincodeRef.current !== "function"
    ) {
      setOptions([]);
      setShowOptions(false);
      setSearching(false);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setSearching(true);

      try {
        const response = await getPincodeRef.current({
          word,
          pageSize: 20,
          nextToken: "",
          previousToken: "",
        });

        if (cancelled) return;

        const matches = extractPincodeRows(response)
          .map(normalizePincode)
          .filter((item) => item.id && item.pinCode);

        setOptions(matches);
        setShowOptions(true);
      } catch (error) {
        if (!cancelled) {
          setOptions([]);
          setShowOptions(false);
          console.error("Pincode search error:", error);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.pinCode, form.pinCodeId]);

  return (
    <KycStepShell title="Driver Address Information" maxWidth="max-w-5xl">
      {/* DRIVER SELECTION */}
      {!editKycDriver && (
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Select Driver <span className="text-red-500">*</span>
          </label>

          <DriverDropdown
            drivers={(drivers || []).map((driver) => ({
              driverId: driver.driverId || driver.driver_code,

              fullName:
                driver.fullName ||
                driver.name ||
                driver.username ||
                driver.driverId ||
                driver.driver_code,
            }))}
            value={form.driver_code}
            onChange={(driverId) =>
              handleDriverSelect({
                target: { value: driverId },
              })
            }
            placeholder="Choose Driver"
            className="w-full"
            dropdownHeight="max-h-[152px]"
          />
        </div>
      )}

      {/* ADDRESS FIELDS */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <KycTextField
          label="City"
          value={form.city}
          placeholder="Enter City"
          onChange={(value) => onFieldChange("city", value)}
        />

        <KycTextField
          label="Area"
          value={form.area}
          placeholder="Enter Area"
          onChange={(value) => onFieldChange("area", value)}
        />

        {/* PINCODE SEARCH */}
        <div className="relative">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Pincode <span className="text-red-500">*</span>
          </label>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={form.pinCode || ""}
            onChange={(event) => {
              const pin = event.target.value.replace(/\D/g, "").slice(0, 6);

              onFieldChange("pinCode", pin, {
                pinCodeId: "",
              });

              setShowOptions(Boolean(pin));
            }}
            onFocus={() => {
              if (options.length && !form.pinCodeId) {
                setShowOptions(true);
              }
            }}
            placeholder="Enter Pincode"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm shadow-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
          />

          {searching && (
            <span className="absolute right-3 top-[43px] text-xs text-gray-400">
              Searching...
            </span>
          )}

          {showOptions && options.length > 0 && !form.pinCodeId && (
            <div className="absolute right-0 z-50 mt-1 max-h-[190px] w-[min(300px,calc(100vw-38px))] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-xl">
              {options.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onFieldChange("pinCode", item.pinCode, {
                      pinCodeId: item.id,
                    });

                    setShowOptions(false);
                  }}
                  className="block w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-orange-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.pinCode}
                        </span>

                        {item.officeType && (
                          <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] text-orange-700">
                            {item.officeType}
                          </span>
                        )}
                      </div>

                      <p className="mt-1 truncate text-xs text-gray-700">
                        {item.officeName || "Office name unavailable"}
                      </p>

                      {(item.division || item.region) && (
                        <p className="mt-1 text-[11px] text-gray-500">
                          {[item.division, item.region]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                      )}

                      {item.circle && (
                        <p className="text-[10px] text-gray-400">
                          {item.circle}
                        </p>
                      )}
                    </div>

                    <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-[10px] text-gray-500">
                      ID {item.id}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <KycNavigation loading={loading} onNext={() => onSubmit(1)} />
    </KycStepShell>
  );
}

// ============================================================
// STEP 2 - PROFILE PHOTO
// ============================================================

function ProfileStep({ form, onFileChange, onSubmit, onBack, loading }) {
  return (
    <KycStepShell title="Upload Profile Picture" maxWidth="max-w-3xl">
      <div className="max-w-sm">
        <KycImageUpload
          field="profile_photo"
          label="Profile Photo"
          value={form.profile_photo}
          onChange={onFileChange}
          rounded
        />
      </div>

      <KycNavigation
        onBack={onBack}
        onNext={() => onSubmit(2)}
        loading={loading}
      />
    </KycStepShell>
  );
}

// ============================================================
// STEP 3 - AADHAAR
// ============================================================

function AadhaarStep({
  form,
  onFieldChange,
  onFileChange,
  onSubmit,
  onBack,
  loading,
}) {
  return (
    <KycStepShell title="Aadhaar Information" maxWidth="max-w-6xl">
      <KycTextField
        label="Aadhaar Number"
        value={form.aadhaar_no}
        maxLength={12}
        placeholder="Enter Aadhaar number"
        onChange={(value) => onFieldChange("aadhaar_no", value)}
      />

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <KycImageUpload
          field="aadhaar_front"
          label="Aadhaar Front"
          value={form.aadhaar_front}
          onChange={onFileChange}
        />

        <KycImageUpload
          field="aadhaar_back"
          label="Aadhaar Back"
          value={form.aadhaar_back}
          onChange={onFileChange}
        />
      </div>

      <KycNavigation
        onBack={onBack}
        onNext={() => onSubmit(3)}
        loading={loading}
      />
    </KycStepShell>
  );
}

// ============================================================
// STEP 4 - PAN CARD
// ============================================================

function PanStep({
  form,
  onFieldChange,
  onFileChange,
  onSubmit,
  onBack,
  loading,
}) {
  return (
    <KycStepShell title="PAN Card Information">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <KycTextField
          label="PAN Card Number"
          value={form.pan_no}
          maxLength={10}
          uppercase
          placeholder="Enter PAN card number"
          onChange={(value) => onFieldChange("pan_no", value)}
        />

        <KycImageUpload
          field="pan_front"
          label="PAN Card Front Image"
          value={form.pan_front}
          onChange={onFileChange}
        />
      </div>

      <KycNavigation
        onBack={onBack}
        onNext={() => onSubmit(4)}
        loading={loading}
      />
    </KycStepShell>
  );
}

// ============================================================
// STEP 5 - DRIVING LICENCE
// ============================================================

function DrivingLicenseStep({
  form,
  onFieldChange,
  onFileChange,
  onSubmit,
  onBack,
  loading,
}) {
  return (
    <KycStepShell title="Driving Licence Information">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <KycTextField
          label="Driving Licence Number"
          value={form.driving_licence}
          maxLength={16}
          uppercase
          placeholder="Enter Driving Licence number"
          onChange={(value) => onFieldChange("driving_licence", value)}
        />

        <KycImageUpload
          field="driving_licence_img"
          label="Driving Licence Image"
          value={form.driving_licence_img}
          onChange={onFileChange}
        />
      </div>

      <KycNavigation
        onBack={onBack}
        onNext={() => onSubmit(5)}
        loading={loading}
      />
    </KycStepShell>
  );
}

// ============================================================
// STEP 6 - VEHICLE DETAILS
// ============================================================

function VehicleStep({
  form,
  onFieldChange,
  onFileChange,
  onSubmit,
  onBack,
  loading,
}) {
  return (
    <KycStepShell title="Vehicle Information" maxWidth="max-w-6xl">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div className="space-y-4">
          <KycTextField
            label="Driving License Number"
            value={form.driving_licence}
            uppercase
            placeholder="Enter Driving License Number"
            onChange={(value) => onFieldChange("driving_licence", value)}
          />

          <KycTextField
            label="Transport Description"
            value={form.transport_description}
            placeholder="Example: Bike, Scooter"
            onChange={(value) => onFieldChange("transport_description", value)}
          />

          <KycTextField
            label="License Plate Number"
            value={form.licence_plate}
            uppercase
            placeholder="Enter License Plate Number"
            onChange={(value) => onFieldChange("licence_plate", value)}
          />
        </div>

        <KycImageUpload
          field="vehicle_name_plate_img"
          label="License Plate Image"
          value={form.vehicle_name_plate_img}
          onChange={onFileChange}
        />
      </div>

      <KycNavigation
        onBack={onBack}
        onNext={() => onSubmit(6)}
        loading={loading}
        last
      />
    </KycStepShell>
  );
}

// ============================================================
// STEP CONTROLLER
// Dynamically displays the current KYC step
// ============================================================

export default function KycStepContent({
  currentStep,
  kycFormdata,
  setKycFormData,
  drivers,
  editKycDriver,
  handleDriverSelect,
  handleFileChange,
  getPincodeDetails,
  submitStep,
  setCurrentStep,
  loading,
}) {
  const onFieldChange = (field, value, extras = {}) => {
    setKycFormData((previous) => ({
      ...previous,
      [field]: value,
      ...extras,
    }));
  };

  const common = {
    form: kycFormdata,
    onFieldChange,
    onFileChange: handleFileChange,
    onSubmit: submitStep,
    onBack: () => setCurrentStep((step) => step - 1),
    loading,
  };

  switch (currentStep) {
    case 1:
      return (
        <AddressStep
          {...common}
          drivers={drivers}
          editKycDriver={editKycDriver}
          handleDriverSelect={handleDriverSelect}
          getPincodeDetails={getPincodeDetails}
        />
      );

    case 2:
      return <ProfileStep {...common} />;

    case 3:
      return <AadhaarStep {...common} />;

    case 4:
      return <PanStep {...common} />;

    case 5:
      return <DrivingLicenseStep {...common} />;

    case 6:
      return <VehicleStep {...common} />;

    default:
      return null;
  }
}
